import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Target, RefreshCw, CheckCircle2, AlertCircle, Loader2, Save, Wand2, Settings, DollarSign, Building2, Library, Calendar, Activity, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminScorecardOrgTab from '@/components/admin/AdminScorecardOrgTab';
import { TemplatesTab, TargetsTab } from '@/components/admin/AdminScorecardTemplatesTargets';
import AdminScorecardHealthCheck from '@/components/admin/AdminScorecardHealthCheck';
import AdminCreativeTeamTab from '@/components/admin/AdminCreativeTeamTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function AdminScorecardTab() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [staffNodes, setStaffNodes] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [pendingPicks, setPendingPicks] = useState({}); // {crm_user_id: staff_node_id}
  const [savingBulk, setSavingBulk] = useState(false);
  const [revenueCfg, setRevenueCfg] = useState(null);
  const [cfgEdit, setCfgEdit] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const [subTab, setSubTab] = useState('sync');
  const [creativeTeamIds, setCreativeTeamIds] = useState([]);

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/scorecard/status`, h());
      setStatus(res.data);
    } catch { /* ignore */ }
  };

  const loadStaffNodes = async () => {
    try {
      const res = await axios.get(`${API}/scorecard/staff-nodes`, h());
      setStaffNodes(res.data.nodes || []);
    } catch { /* ignore */ }
  };

  const loadMappings = async () => {
    try {
      const res = await axios.get(`${API}/scorecard/mappings`, h());
      setMappings(res.data.mappings || []);
    } catch { /* ignore */ }
  };

  const loadRevenueCfg = async () => {
    try {
      const res = await axios.get(`${API}/scorecard/revenue-config`, h());
      setRevenueCfg(res.data);
      setCfgEdit({
        total_monthly_target_myr: res.data.total_monthly_target_myr,
        splits: { ...res.data.splits },
      });
    } catch { /* ignore */ }
  };

  const loadCreativeTeam = async () => {
    try {
      const res = await axios.get(`${API}/creative-scorecard/team-members`, h());
      setCreativeTeamIds(res.data?.member_ids || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadStatus();
    loadStaffNodes();
    loadMappings();
    loadRevenueCfg();
    loadCreativeTeam();
  }, []);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/scorecard/sync`, {}, h());
      toast.success(`Synced: ${res.data.templates} templates, ${res.data.nodes} nodes, ${res.data.entries} entries`);
      await loadStatus();
      await loadStaffNodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const loadSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API}/scorecard/mappings/auto-suggest`, h());
      setSuggestions(res.data.suggestions || []);
      // Pre-populate pendingPicks with auto-picks (single → array)
      const picks = {};
      (res.data.suggestions || []).forEach(s => {
        if (s.auto_pick) picks[s.crm_user_id] = [s.auto_pick.staff_node_id];
      });
      // Merge in existing multi-mappings (preserve current admin choices)
      mappings.forEach(m => {
        const ids = m.staff_node_ids || (m.staff_node_id ? [m.staff_node_id] : []);
        if (ids.length > 0) picks[m.crm_user_id] = ids;
      });
      setPendingPicks(picks);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const saveBulkMappings = async () => {
    const items = Object.entries(pendingPicks)
      .filter(([, sids]) => Array.isArray(sids) && sids.length > 0)
      .map(([crm_user_id, staff_node_ids]) => {
        const primary = staffNodes.find(n => n.id === staff_node_ids[0]);
        return { crm_user_id, staff_node_ids, staff_name: primary?.name || '' };
      });
    if (items.length === 0) {
      toast.error('No mappings selected');
      return;
    }
    setSavingBulk(true);
    try {
      const res = await axios.post(`${API}/scorecard/mappings/bulk`, { mappings: items }, h());
      toast.success(`Saved ${res.data.saved} mappings`);
      await loadMappings();
      await loadSuggestions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingBulk(false);
    }
  };

  const removeMapping = async (crm_user_id) => {
    try {
      await axios.put(`${API}/scorecard/mappings/${crm_user_id}`, { staff_node_ids: [] }, h());
      toast.success('Mapping removed');
      await loadMappings();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const saveRevenueCfg = async () => {
    setSavingCfg(true);
    try {
      const res = await axios.put(`${API}/scorecard/revenue-config`, cfgEdit, h());
      const sum = res.data.split_sum_pct;
      if (Math.abs(sum - 100) > 0.01) {
        toast.warning(`Saved — but split sum is ${sum}% (expected 100%)`);
      } else {
        toast.success('Revenue config saved');
      }
      await loadRevenueCfg();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingCfg(false);
    }
  };

  const mappingByUser = useMemo(() => {
    const m = {};
    mappings.forEach(x => { m[x.crm_user_id] = x; });
    return m;
  }, [mappings]);

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-MY', { hour12: false });
    } catch { return iso; }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center">
          <Target size={20} className="text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">Scorecard Integration</h2>
          <p className="text-xs text-[hsl(var(--text-muted))]">External KPI Scorecard sync, user mapping, revenue config</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-[hsl(var(--border))]">
        {[
          { id: 'sync', label: 'Sync & Mapping', icon: RefreshCw },
          { id: 'organization', label: 'Organization', icon: Building2 },
          { id: 'templates', label: 'Templates', icon: Library },
          { id: 'targets', label: 'Targets', icon: Calendar },
          { id: 'creative-team', label: 'Creative Team', icon: Palette },
          { id: 'health', label: 'Health Check', icon: Activity },
        ].map(t => {
          const Icon = t.icon;
          const active = subTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              data-testid={`scorecard-subtab-${t.id}`}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 transition ${
                active
                  ? 'border-[#10B981] text-white'
                  : 'border-transparent text-[#6B7280] hover:text-white'
              }`}
            >
              <Icon size={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {subTab === 'organization' && <AdminScorecardOrgTab />}
      {subTab === 'templates' && <TemplatesTab />}
      {subTab === 'targets' && <TargetsTab />}
      {subTab === 'creative-team' && <AdminCreativeTeamTab />}
      {subTab === 'health' && <AdminScorecardHealthCheck />}

      {subTab === 'sync' && <>

      {/* Sunset banner — external link deprecation notice */}
      <div className="bg-gradient-to-r from-[#F59E0B]/10 to-[#E63946]/5 border border-[#F59E0B]/30 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="text-[#F59E0B] flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1">
          <p className="text-white text-sm font-semibold mb-1">External Scorecard akan deprecated bulan depan</p>
          <p className="text-xs text-[#9CA3AF]">
            Mulai bulan depan, edit org tree / templates / targets terus dalam CRM:
            <span className="text-[#10B981] mx-1">Organization</span>·
            <span className="text-[#10B981] mx-1">Templates</span>·
            <span className="text-[#10B981] mx-1">Targets</span>.
            Sync external (bawah) hanya untuk one-last-time refresh kalau perlu. Selepas tu, takkan sync lagi.
          </p>
        </div>
      </div>

      {/* Sync card */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
              <RefreshCw size={14} /> Snapshot Sync
            </h3>
            <p className="text-[11px] text-[hsl(var(--text-muted))] mb-2">
              Pulls templates (KPI metrics) + org tree + all entries from external Scorecard API.
            </p>
            {status && (
              <div className="flex flex-wrap gap-2 text-[11px]">
                {status.synced ? (
                  <>
                    <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
                      <CheckCircle2 size={11} className="mr-1" /> Synced
                    </Badge>
                    <span className="text-[hsl(var(--text-muted))]">Last fetched: {fmtTime(status.fetched_at)}</span>
                    {status.counts && (
                      <>
                        <Badge variant="outline" className="text-[10px]">{status.counts.templates} templates</Badge>
                        <Badge variant="outline" className="text-[10px]">{status.counts.nodes} nodes</Badge>
                        <Badge variant="outline" className="text-[10px]">{status.counts.entries} entries</Badge>
                      </>
                    )}
                  </>
                ) : (
                  <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30">
                    <AlertCircle size={11} className="mr-1" /> Not synced yet
                  </Badge>
                )}
              </div>
            )}
          </div>
          <Button onClick={triggerSync} disabled={syncing} data-testid="scorecard-sync-btn"
            className="bg-violet-600 hover:bg-violet-700 text-white">
            {syncing ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
            Sync Now
          </Button>
        </div>
      </div>

      {/* Revenue Config */}
      {revenueCfg && (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
            <DollarSign size={14} /> Revenue Target Config
            {revenueCfg.is_default && <Badge variant="outline" className="text-[9px]">DEFAULT</Badge>}
          </h3>
          <p className="text-[11px] text-[hsl(var(--text-muted))] mb-3">
            Default RM 800,000/month with 90/5/5 split. Override only if business model changes.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-3">
            <div className="lg:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">Total Monthly Target (MYR)</label>
              <Input type="number" min="0" step="1000" value={cfgEdit.total_monthly_target_myr || 0}
                onChange={e => setCfgEdit(c => ({ ...c, total_monthly_target_myr: parseFloat(e.target.value || 0) }))}
                className="mt-1" data-testid="cfg-total-target" />
            </div>
            {Object.entries(cfgEdit.splits || {}).map(([key, val]) => (
              <div key={key}>
                <label className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))]">{val.label} (%)</label>
                <Input type="number" min="0" max="100" step="0.1" value={val.percentage || 0}
                  onChange={e => setCfgEdit(c => ({
                    ...c,
                    splits: { ...c.splits, [key]: { ...c.splits[key], percentage: parseFloat(e.target.value || 0) } },
                  }))}
                  className="mt-1" data-testid={`cfg-pct-${key}`} />
                <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">
                  ≈ RM {(((cfgEdit.total_monthly_target_myr || 0) * (val.percentage || 0)) / 100).toLocaleString('en-MY', { maximumFractionDigits: 0 })}
                </p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-[hsl(var(--text-muted))]">
              Sum of splits: <span className={Object.values(cfgEdit.splits || {}).reduce((s, v) => s + (v.percentage || 0), 0).toFixed(1) === '100.0' ? 'text-emerald-400' : 'text-amber-400'}>
                {Object.values(cfgEdit.splits || {}).reduce((s, v) => s + (v.percentage || 0), 0).toFixed(1)}%
              </span>
            </p>
            <Button onClick={saveRevenueCfg} disabled={savingCfg} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cfg-save-btn">
              {savingCfg ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              Save Config
            </Button>
          </div>
        </div>
      )}

      {/* Mapping section */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
              <Settings size={14} /> CRM User → Scorecard STAFF Mapping
            </h3>
            <p className="text-[11px] text-[hsl(var(--text-muted))]">
              Link each CRM user to their Scorecard staff node so they can see their own KPIs in My Space.
            </p>
          </div>
          <Button onClick={loadSuggestions} disabled={loadingSuggestions || !status?.synced}
            className="bg-violet-600 hover:bg-violet-700" data-testid="suggest-btn">
            {loadingSuggestions ? <Loader2 size={14} className="animate-spin mr-2" /> : <Wand2 size={14} className="mr-2" />}
            Auto-Suggest
          </Button>
        </div>

        {/* Creative team notice */}
        {creativeTeamIds.length > 0 && (
          <div className="bg-pink-500/10 border border-pink-500/20 rounded-md p-3 text-[11px] text-pink-200 mb-3 flex items-start gap-2" data-testid="creative-team-mapping-notice">
            <Palette size={12} className="flex-shrink-0 mt-0.5 text-pink-400" />
            <div>
              <strong>Creative Team Members</strong> ({creativeTeamIds.length}) — these users <strong>do NOT need manual STAFF mapping</strong>.
              They automatically see all per-channel &quot;Creative&quot; nodes in their <em>My Scorecard</em> via the Creative View.
              Manage them under <strong>Creative Team</strong> sub-tab. You may leave their &quot;Pick staff node&quot; blank.
            </div>
          </div>
        )}

        {/* Suggestions table */}
        {suggestions.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-lg border border-[hsl(var(--border))]">
              <table className="w-full text-[11px]">
                <thead className="bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">CRM User</th>
                    <th className="text-left px-3 py-2">Department</th>
                    <th className="text-left px-3 py-2">Top Match</th>
                    <th className="text-left px-3 py-2">Pick</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map(s => {
                    const existing = mappingByUser[s.crm_user_id];
                    return (
                      <tr key={s.crm_user_id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))]/50">
                        <td className="px-3 py-2 font-semibold text-white">
                          {s.crm_user_name}
                          {creativeTeamIds.includes(s.crm_user_id) && (
                            <Badge className="ml-2 bg-pink-500/15 text-pink-300 border border-pink-500/30 text-[9px]" data-testid={`creative-badge-${s.crm_user_id}`}>CREATIVE TEAM</Badge>
                          )}
                          {existing && <Badge className="ml-2 bg-emerald-500/15 text-emerald-400 text-[9px]">MAPPED</Badge>}
                        </td>
                        <td className="px-3 py-2 text-[hsl(var(--text-muted))]">{s.department || '—'}</td>
                        <td className="px-3 py-2">
                          {s.candidates.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {s.candidates.slice(0, 3).map(c => (
                                <span key={c.staff_node_id} className="text-[10px]">
                                  <span className={c.score >= 0.7 ? 'text-emerald-400' : c.score >= 0.5 ? 'text-amber-400' : 'text-[hsl(var(--text-muted))]'}>
                                    {(c.score * 100).toFixed(0)}%
                                  </span>{' '}
                                  <span className="text-white">{c.staff_name}</span>
                                </span>
                              ))}
                            </div>
                          ) : <span className="text-[hsl(var(--text-muted))]">no match</span>}
                        </td>
                        <td className="px-3 py-2 min-w-[320px]">
                          {(() => {
                            const picks = pendingPicks[s.crm_user_id] || [];
                            return (
                              <div className="space-y-1.5">
                                {picks.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {picks.map(pid => {
                                      const node = staffNodes.find(n => n.id === pid);
                                      return (
                                        <span key={pid} className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded-full">
                                          {node?.name || pid.slice(0, 8)}
                                          <button
                                            onClick={() => setPendingPicks(p => ({ ...p, [s.crm_user_id]: (p[s.crm_user_id] || []).filter(x => x !== pid) }))}
                                            className="hover:text-emerald-100 ml-0.5"
                                            data-testid={`pick-remove-${s.crm_user_id}-${pid.slice(0,8)}`}
                                          >×</button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                <Select value="" onValueChange={v => {
                                  if (!v || v === 'none') return;
                                  setPendingPicks(p => {
                                    const cur = p[s.crm_user_id] || [];
                                    if (cur.includes(v)) return p;
                                    return { ...p, [s.crm_user_id]: [...cur, v] };
                                  });
                                }}>
                                  <SelectTrigger className="h-7 text-[11px]" data-testid={`pick-${s.crm_user_id}`}>
                                    <SelectValue placeholder={picks.length === 0 ? "-- Pick staff node --" : "+ Add another channel"} />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[400px]">
                                    {(() => {
                                      // Build set of node_ids already picked by OTHER users
                                      const usedByOthers = new Set();
                                      Object.entries(pendingPicks).forEach(([uid, ids]) => {
                                        if (uid === s.crm_user_id) return;
                                        (ids || []).forEach(id => usedByOthers.add(id));
                                      });
                                      const myPicks = pendingPicks[s.crm_user_id] || [];
                                      return staffNodes
                                        .filter(n => !myPicks.includes(n.id) && !usedByOthers.has(n.id))
                                        .map(n => (
                                          <SelectItem key={n.id} value={n.id}>
                                            <span className="text-[10px]">[{n.type}]</span> {n.name}
                                            {n.parent_chain && <span className="text-[9px] text-[hsl(var(--text-muted))]"> · {n.parent_chain.slice(0, 50)}</span>}
                                          </SelectItem>
                                        ));
                                    })()}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={saveBulkMappings} disabled={savingBulk} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-bulk-btn">
                {savingBulk ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                Save All Picks
              </Button>
            </div>
          </>
        )}

        {/* Existing mappings */}
        {mappings.length > 0 && (
          <div className="mt-5">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--text-muted))] mb-2">
              Current Mappings ({mappings.length})
            </h4>
            <div className="overflow-x-auto rounded-lg border border-[hsl(var(--border))]">
              <table className="w-full text-[11px]">
                <thead className="bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">CRM User</th>
                    <th className="text-left px-3 py-2">Scorecard Staff Nodes</th>
                    <th className="text-left px-3 py-2">Updated</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map(m => {
                    const nodeIds = m.staff_node_ids || (m.staff_node_id ? [m.staff_node_id] : []);
                    return (
                    <tr key={m.crm_user_id} className="border-t border-[hsl(var(--border))]">
                      <td className="px-3 py-2 font-semibold text-white">{m.crm_user_name}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {nodeIds.map(nid => {
                            const node = staffNodes.find(n => n.id === nid);
                            return (
                              <span key={nid} className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] px-1.5 py-0.5 rounded-full">
                                {node?.name || nid.slice(0, 8)}
                              </span>
                            );
                          })}
                          {nodeIds.length === 0 && <span className="text-[hsl(var(--text-muted))]">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[hsl(var(--text-muted))]">{fmtTime(m.updated_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button onClick={() => {
                            setPendingPicks(p => ({ ...p, [m.crm_user_id]: nodeIds }));
                            // Scroll to suggestions area
                            toast.info(`Edit mode: ${m.crm_user_name}. Scroll up to Suggestions table to add/remove nodes.`);
                          }} variant="outline" size="sm" className="h-7 text-[10px] border-blue-500/30 text-blue-400" data-testid={`edit-mapping-${m.crm_user_id}`}>
                            Edit
                          </Button>
                          <Button onClick={() => removeMapping(m.crm_user_id)} variant="outline" size="sm" className="h-7 text-[10px]" data-testid={`remove-${m.crm_user_id}`}>
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {suggestions.length === 0 && mappings.length === 0 && (
          <div className="text-center py-8 text-[hsl(var(--text-muted))] text-[12px]">
            {status?.synced ? 'Click "Auto-Suggest" to see suggested mappings.' : 'Sync the scorecard first to enable mapping.'}
          </div>
        )}
      </div>
      </>}
    </div>
  );
}
